'use client'
import { type FC, useMemo } from 'react'
import type { CompanyAddressDTO, OrderAddressDTO } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUser } from 'auth'
import { useRememberedAddress } from '../../../hooks'
import { CompanyAddressPicker } from './CompanyAddressPicker'

// Override keys keep the legacy `invoice*` prefix so the existing
// `invoicePaymentTranslations` bundle resolves them across all flows.
export interface ShippingAddressPickerOverrides {
  invoiceShippingHeading?: string
  invoiceShippingFoldedHint?: string
  invoiceShippingChange?: string
  invoiceShippingCancel?: string
  invoiceShippingSaveCustom?: string
  invoiceShippingCustomOption?: string
  invoiceShippingEmpty?: string
  // Field labels are shared with billing — same OrderAddressDTO shape.
  invoiceBillingFieldAttention?: string
  invoiceBillingFieldAddress1?: string
  invoiceBillingFieldAddress2?: string
  invoiceBillingFieldPostalCode?: string
  invoiceBillingFieldPostalRegion?: string
  invoiceBillingFieldCountry?: string
  invoiceBillingFieldMobilePhone?: string
  invoiceBillingFieldEmail?: string
  invoiceBillingErrorPhone?: string
  invoiceBillingFieldFirstName?: string
  invoiceBillingFieldLastName?: string
  invoiceBillingErrorRequired?: string
  invoiceBillingErrorEmail?: string
  invoiceBillingFieldReference?: string
  invoiceBillingRememberOnDevice?: string
}

interface ShippingAddressPickerProps {
  /** Render the free-text reference field. Default on; off where nothing reads it. */
  showReference?: boolean
  /** Render the phone field. Default on; off when the payer's phone is collected elsewhere. */
  showPhone?: boolean
  value: OrderAddressDTO | undefined
  onChange: (next: OrderAddressDTO) => void
}

/**
 * Shipping-address picker shared by the invoice and upfront-DEFAULT flows.
 * Mirrors `BillingAddressPicker` but filters the customer's company
 * addresses to `type === 'SHIPPING'`. Field labels reuse the billing
 * translation keys since the `OrderAddressDTO` shape is the same; only the
 * heading / hint / custom-option copy is shipping-specific.
 */
export const ShippingAddressPicker: FC<ShippingAddressPickerProps> = ({
  value,
  onChange,
  showReference,
  showPhone
}) => {
  const overrides = useOverridesContext<keyof ShippingAddressPickerOverrides>()
  const { user } = useUser()
  // Records which option the shopper settles on so it comes back preselected. Reading
  // it happens in `DefaultDeliveryCheckout`, which owns the precedence; here we only write.
  const { remember, isOptedIn, setOptedIn } = useRememberedAddress('shipping')

  const shippingAddresses = useMemo<CompanyAddressDTO[]>(
    () => (user?.customer?.companyProfile?.addresses ?? []).filter((a) => a.type === 'SHIPPING'),
    [user?.customer?.companyProfile?.addresses]
  )

  const labels = {
    heading: overrideText('Leveransadress', overrides?.invoiceShippingHeading),
    foldedHint: overrideText('Vald adress', overrides?.invoiceShippingFoldedHint),
    change: overrideText('Ändra', overrides?.invoiceShippingChange),
    cancel: overrideText('Avbryt', overrides?.invoiceShippingCancel),
    save: overrideText('Spara adress', overrides?.invoiceShippingSaveCustom),
    customOption: overrideText('Anpassad adress', overrides?.invoiceShippingCustomOption),
    empty: overrideText('Ingen leveransadress vald.', overrides?.invoiceShippingEmpty),
    attention: overrideText('Att / Kontaktperson', overrides?.invoiceBillingFieldAttention),
    address1: overrideText('Adress', overrides?.invoiceBillingFieldAddress1),
    address2: overrideText('Adress rad 2', overrides?.invoiceBillingFieldAddress2),
    postalCode: overrideText('Postnummer', overrides?.invoiceBillingFieldPostalCode),
    postalRegion: overrideText('Ort', overrides?.invoiceBillingFieldPostalRegion),
    country: overrideText('Land', overrides?.invoiceBillingFieldCountry),
    mobilePhone: overrideText('Telefon', overrides?.invoiceBillingFieldMobilePhone),
    email: overrideText('E-post', overrides?.invoiceBillingFieldEmail),
    phoneError: overrideText('Ogiltigt telefonnummer', overrides?.invoiceBillingErrorPhone),
    firstName: overrideText('Förnamn', overrides?.invoiceBillingFieldFirstName),
    lastName: overrideText('Efternamn', overrides?.invoiceBillingFieldLastName),
    requiredError: overrideText('Obligatoriskt fält', overrides?.invoiceBillingErrorRequired),
    emailError: overrideText('Ogiltig e-postadress', overrides?.invoiceBillingErrorEmail),
    reference: overrideText('Referens', overrides?.invoiceBillingFieldReference),
    rememberOnDevice: overrideText(
      'Kom ihåg mitt val på den här enheten',
      overrides?.invoiceBillingRememberOnDevice
    )
  }

  return (
    <CompanyAddressPicker
      addresses={shippingAddresses}
      labels={labels}
      onChange={onChange}
      onChoose={remember}
      rememberOnDevice={{ checked: isOptedIn, onChange: setOptedIn }}
      showPhone={showPhone}
      showReference={showReference}
      value={value}
    />
  )
}
